import axios from 'axios';

export const PRACTICE_LANGUAGES = [
  'c',
  'cpp',
  'python',
  'java',
  'javascript',
  'html',
  'css',
];

export const LANGUAGE_LABELS = {
  c: 'C',
  cpp: 'C++',
  python: 'Python',
  java: 'Java',
  javascript: 'JavaScript',
  html: 'HTML',
  css: 'CSS',
};

export function isPgdaCourse(courseId) {
  return String(courseId || '').trim().toLowerCase() === 'pgdca';
}

/** Same API default as Flutter student app (production unless REACT_APP_API_URL is set). */
export function getStudentAppApiBaseUrl() {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  return 'https://services.knls.in/api';
}

export function getStudentAuthHeaders() {
  const token = localStorage.getItem('student_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function getStudentPhone() {
  try {
    const raw = localStorage.getItem('student_data');
    const parsed = raw ? JSON.parse(raw) : null;
    return String(parsed?.phone || '').replace(/\D/g, '').slice(-10);
  } catch {
    return '';
  }
}

export async function loadFallbackRunContentId(courseId) {
  const response = await axios.get(
    `${getStudentAppApiBaseUrl()}/lms/courses/${courseId}/builder`,
    {
      headers: getStudentAuthHeaders(),
      params: { publishedOnly: true },
    },
  );
  const chapters = (response.data?.chapters || []).filter(
    (ch) => ch.status === 'published',
  );
  return findFallbackRunContentId(chapters);
}

export function findFallbackRunContentId(chapters = []) {
  for (const chapter of chapters) {
    const items = chapter.items?.filter((it) => it.status === 'published') || [];
    for (const item of items) {
      const type = String(item.type || item.viewerType || '').toLowerCase();
      const hasCoding =
        type === 'coding' ||
        item.coding ||
        item.contentData?.codingData ||
        item.contentData?.coding;
      if (!hasCoding) continue;

      const coding =
        item.coding ||
        item.contentData?.codingData ||
        item.contentData?.coding ||
        {};
      if (coding.allowRun === false) continue;

      const id = item.dbId || item.content_id || item.contentId;
      if (id) return id;
    }
  }
  return null;
}

export function estimateInteractiveInputs(code, language) {
  const lang = String(language || '').toLowerCase();
  if (lang === 'python') {
    return (code.match(/\binput\s*\(/g) || []).length;
  }
  if (lang === 'c' || lang === 'cpp') {
    return (code.match(/\bscanf\s*\(/g) || []).length;
  }
  if (lang === 'java') {
    return (code.match(/\.next(?:Line|Int|Double|Float)?\s*\(/g) || []).length;
  }
  if (lang === 'javascript') {
    return (code.match(/\breadline\s*\(/g) || []).length;
  }
  return 0;
}

export function prepareStdin(raw) {
  if (!raw) return '';
  let text = String(raw).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (!text.endsWith('\n')) {
    text = `${text}\n`;
  }
  return text;
}

export function stdinInputHint(inputCount) {
  if (inputCount <= 0) {
    return '';
  }
  if (inputCount === 1) {
    return 'Type your answer in the terminal when prompted.';
  }
  return `Type ${inputCount} answers in the terminal when prompted (one per prompt).`;
}

function unescapeCString(text) {
  return String(text || '')
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t')
    .replace(/\\r/g, '\r')
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, '\\');
}

/** Best-effort prompt text shown before scanf / input() in student programs. */
export function extractInputPrompts(code, language) {
  const lang = String(language || '').toLowerCase();
  const source = String(code || '');

  if (lang === 'c' || lang === 'cpp') {
    const prompts = [];
    const inputRegex = /\b(scanf|gets|fgets|getchar)\s*\(/gi;
    let match;
    while ((match = inputRegex.exec(source)) !== null) {
      const before = source.slice(0, match.index);
      const printfRegex = /printf\s*\(\s*"((?:[^"\\]|\\.)*)"\s*(?:,[^)]*)?\)/gi;
      let lastPrompt = '';
      let printfMatch;
      while ((printfMatch = printfRegex.exec(before)) !== null) {
        lastPrompt = unescapeCString(printfMatch[1]);
      }
      prompts.push(lastPrompt);
    }
    return prompts;
  }

  if (lang === 'python') {
    const prompts = [];
    const inputRegex = /\binput\s*\(/g;
    let match;
    while ((match = inputRegex.exec(source)) !== null) {
      const before = source.slice(0, match.index);
      const printRegex = /print\s*\(\s*(?:"((?:[^"\\]|\\.)*)"|'((?:[^'\\]|\\.)*)')/g;
      let lastPrompt = '';
      let printMatch;
      while ((printMatch = printRegex.exec(before)) !== null) {
        lastPrompt = unescapeCString(printMatch[1] || printMatch[2] || '');
      }
      prompts.push(lastPrompt);
    }
    return prompts;
  }

  if (lang === 'java') {
    const prompts = [];
    const inputRegex = /\.next(?:Line|Int|Double|Float|Long)?\s*\(/g;
    let match;
    while ((match = inputRegex.exec(source)) !== null) {
      const before = source.slice(0, match.index);
      const printRegex = /System\.out\.print(?:ln)?\s*\(\s*"((?:[^"\\]|\\.)*)"/g;
      let lastPrompt = '';
      let printMatch;
      while ((printMatch = printRegex.exec(before)) !== null) {
        lastPrompt = unescapeCString(printMatch[1]);
      }
      prompts.push(lastPrompt);
    }
    return prompts;
  }

  return [];
}

export function longestCommonPrefix(a, b) {
  const left = String(a || '');
  const right = String(b || '');
  let index = 0;
  const limit = Math.min(left.length, right.length);
  while (index < limit && left[index] === right[index]) {
    index += 1;
  }
  return left.slice(0, index);
}

/** Show prompt on its own line so the student's answer appears below it. */
export function formatPromptForDisplay(prompt) {
  if (!prompt) return '';
  if (prompt.endsWith('\n')) return prompt;
  return `${prompt}\n`;
}

/** Program output that appears after all prompts and scanf echoes. */
export function getRemainingStdout(fullStdout, prompts = []) {
  const combined = prompts.join('');
  const stdout = String(fullStdout || '');
  if (combined && stdout.startsWith(combined)) {
    return stdout.slice(combined.length);
  }
  return stdout;
}

export function formatResultSuffix(suffix) {
  const text = String(suffix || '');
  if (!text) return '';
  if (text.startsWith('\n')) return text;
  return `\n${text}`;
}

export function transcriptToDisplayText(transcript = []) {
  return transcript
    .map((entry) => {
      if (entry.type === 'in') {
        return entry.text;
      }
      return entry.text;
    })
    .join('');
}

export function formatRunOutput(result = {}) {
  const stdout = String(result.stdout || '');
  const stderr = String(result.stderr || '');
  const compileOutput = String(result.compileOutput || '');
  const apiError = String(result.error || '');
  const exitCode = result.exitCode;

  if (exitCode === -1 && apiError && apiError !== 'Error') {
    return { output: stdout, error: apiError };
  }

  if (
    exitCode === -1 &&
    (stderr === 'Error' || apiError === 'Error' || (!stdout && !stderr && !compileOutput))
  ) {
    return {
      output: stdout,
      error:
        'Code runner is not available. The server could not execute your code. ' +
        'If you are on local dev, start Piston (port 2000) or set PISTON_API_URL on the backend.',
    };
  }

  const stdinHelp = friendlyStdinError(stderr, 0);
  if (stdinHelp) {
    return { output: stdout, error: stdinHelp };
  }

  const failed = typeof exitCode === 'number' && exitCode !== 0;
  const lines = [
    stdout,
    stderr,
    compileOutput ? `compile:\n${compileOutput}` : '',
    failed && apiError && !stderr.includes(apiError) ? apiError : '',
  ].filter(Boolean);

  if (failed && lines.length === 0) {
    return { output: '', error: apiError || 'Program exited with an error.' };
  }

  return { output: lines.join('\n\n'), error: '' };
}

export function friendlyStdinError(stderr, inputCount) {
  const lower = String(stderr || '').toLowerCase();
  if (
    lower.includes("could not convert string to float: ''") ||
    lower.includes('invalid literal for int()') ||
    lower.includes('eof when reading a line') ||
    lower.includes('unexpected eof')
  ) {
    if (inputCount > 0) {
      return (
        `Program expected input in the terminal. Enter ${inputCount} value(s), one per line, then click Run again.\n\n` +
        'Example for 2 inputs:\n5\n10'
      );
    }
    return 'Program expected input in the terminal before Run.';
  }
  return null;
}
